import { v4 as uuidv4 } from 'uuid';
import { FC, useEffect, useState } from "react";
import { InvoiceOutCreator } from "..";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import { IInvoiceOutCreateDTO, IInvoiceOutGetDTO, IInvoiceOutUpdateDTO } from "../../../../libs/coreapi-dto/documents/invoiceOut";
import { InvoiceOutDataProvider } from "../../../../Services/DataProviders/InvoiceOutDataProvider";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { getInvoiceOutItemUpdateDTO } from '../../../../libs/core-api-requests/customRequest/InvoiceOutItemUpdateRequest';
import { useUserContext } from '../../../../system/providers/userContextProvider';
import { DocumentOperationSpinner } from '../../../../components/DocumentOperationSpinner/DocumentOperationSpinner';

interface ICreateDocument {
    callback: (entity: IInvoiceOutUpdateDTO, withClose: boolean, view: GridStateType) => void
}

interface IEditDocument {
    callback: (id: string, entity: IInvoiceOutUpdateDTO, withClose: boolean) => void
    entity: IInvoiceOutGetDTO
}

interface ICopyDocument {
    callback: (entity: IInvoiceOutUpdateDTO, withClose: boolean) => void
    entity: IInvoiceOutGetDTO
}

export const InvoiceOutCreatorView: FC<IDocumentCreatorProps<IInvoiceOutCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext()
    const tabsContext = useTabsContext();

    const idp = new InvoiceOutDataProvider(appContext.coreApiService);
    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [id, setId] = useState<string | undefined>(props.idGlobal ?? uuidv4());
    const [idGlobalCopy, setIdGlobalCopy] = useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [createDocument, setCreateDocument] = useState<ICreateDocument | null>(null);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);
    const [copyDocument, setCopyDocument] = useState<ICopyDocument | null>(null);

    //#region renderForm()

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true)
            
            if (viewState === "create") {
                const createCallback = (entity: IInvoiceOutUpdateDTO, withClose: boolean, view: GridStateType) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    idp.create(entity, (id) => {
                        setId(id);
                        setViewState(view);
                        props.createdCallback?.();
                        withClose && tabToClose && tabsContext.close(tabToClose);
                        setIsSubmitting(false);
                    }, ()=>{}, (e)=>{
                        setIsSubmitting(false);
                    });
                };
                setCreateDocument({ callback: createCallback });
                setIsLoading(false);
            }
    
            else if (viewState === "edit") {
                const editCallback = (
                    id: string,
                    entity: IInvoiceOutUpdateDTO,
                    withClose: boolean
                ) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    idp.update(id as string, entity, (e) => {
                        if (e.respType === 'isFailed') return setIsSubmitting(false);
                           
                        props.createdCallback?.(entity);
                        if (withClose && tabToClose) {
                            tabsContext.close(tabToClose);
                        }
                        setIsSubmitting(false);
                    });
                };
                try {
                    await new Promise<void>((resolve, reject) => {
                        idp.getById(id as string, async (entity) => {
                            getInvoiceOutItemUpdateDTO(entity.idGlobal as string, e => {
                                setEditDocument({
                                    callback: editCallback,
                                    entity: {
                                        ...entity,
                                        documentDate: entity.documentDate.toF3DateTimeServerSide(userContext),
                                        items: e
                                    },
                                });
                                resolve();
                            })
                        });
                    }

                    )
                } catch (error) {
                    console.error('Error loading document:', error);
                }
                finally {
                    setIsLoading(false);
                }
            }
    
            else if (viewState === "copy") {
                let idGlobal = uuidv4()
                setIdGlobalCopy(idGlobal)
                const createCallback = (
                    entity: IInvoiceOutUpdateDTO,
                    withClose: boolean
                ) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    idp.create(entity, (id) => {
                        setId(id);
                        setViewState("edit");
                        props.createdCallback?.();
                        withClose && tabToClose && tabsContext.close(tabToClose);
                        setIsSubmitting(false);
                    }, ()=>{}, (e)=>{
                        setIsSubmitting(false);
                    });
                };
                try {
                    await new Promise<void>((resolve, reject) => {
                        idp.getById(id as string, (entity) => {
                            const itemsCopy = entity.items.map((item) => ({
                                ...item,
                                idGlobal: uuidv4(),
                                kizs: [],
                                kizBoxes: [],
                                kizBoxesGisMt: [],
                                kizOsuDtos: [],
                                kizOsuDto: undefined,
                            }));
                            setCopyDocument({ callback: createCallback, entity: {
                                ...entity,
                                idGlobal: idGlobal,
                                documentDate: entity.documentDate.toF3DateTimeServerSide(userContext),
                                mnemocode: '',
                                items: itemsCopy,
                                documentState: 'draft'
                            }});
                            resolve();
                        });
                    });
                }
                catch (error) {
                    console.error('Error loading document:', error);
                }
                finally {
                    setIsLoading(false);
                }
            }
        }
        initializeComponent()
    }, [viewState]);

    if (isLoading) {
            return (
                <div >
                    <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />                
                </div>
            );
        }

    return (
        <>
            {viewState === "create" ? (
                createDocument && (
                    <InvoiceOutCreator
                        variant="create"
                        isSubmitting={isSubmitting}
                        idGlobal={id}
                        save={(entity) => createDocument.callback(entity, false, "edit")}
                        ok={(entity) => createDocument.callback(entity, true, "create")}
                        cancel={() => tabsContext.closeCurrent()}
                        permission={props.permission}
                    />
                )
            ) : viewState === "edit" ? (
                editDocument && (
                    <InvoiceOutCreator
                        idGlobal={id}
                        variant="edit"
                        document={editDocument.entity}
                        isSubmitting={isSubmitting}
                        save={(value) => editDocument.callback(id as string, value, false)}
                        ok={(value) => editDocument.callback(id as string, value, true)}
                        cancel={() => tabsContext.closeCurrent()}
                        permission={props.permission}
                    />
                )
            ) : viewState === "copy" ? (
                copyDocument && (
                    <InvoiceOutCreator
                        idGlobal={idGlobalCopy}
                        variant="copy"
                        document={copyDocument.entity}
                        isSubmitting={isSubmitting}
                        save={(value) => copyDocument.callback(value, false)}
                        ok={(value) => copyDocument.callback(value, true)}
                        cancel={() => tabsContext.closeCurrent()}
                        permission={props.permission}
                    />
                )
            ) : (
                <></>
            )}
        </>
    );
};

export default InvoiceOutCreatorView;
